/**
 * Workspace labels tab (FRIDAY-65, FRIDAY-105).
 *
 * Labels belong to the workspace. Each label is available on every board by
 * default; the Boards control limits it to some boards.
 */

import { useEffect, useState } from 'react'
import { ApiError } from '@/lib/api'
import { toast } from '@/features/feedback/toast'
import {
  useArchiveLabel,
  useCreateLabel,
  useUpdateLabel,
  useWorkspaceLabels,
  type WorkspaceLabel,
  type WorkspaceLabelBoard,
} from '@/features/labels/labels'
import { NavigationGuardProvider, useUnsavedChangesGuard } from '@/features/settings/useUnsavedChangesGuard'
import { ConfirmDialog } from '@/components/feedback'
import { MultiSelectMenu } from '@/components/ui/MultiSelectMenu'
import { cn } from '@/lib/utils'

const PAGE_SIZE = 25

export interface WorkspaceLabelsPanelProps {
  wsId: string
  /**
   * Locks the panel to one board. The board settings screen uses it to
   * show the labels of that board; the workspace settings leave it open.
   */
  projectId?: string
}

export function WorkspaceLabelsPanel({ wsId, projectId }: WorkspaceLabelsPanelProps) {
  return (
    <NavigationGuardProvider>
      <WorkspaceLabelsPanelBody wsId={wsId} projectId={projectId} />
    </NavigationGuardProvider>
  )
}

function WorkspaceLabelsPanelBody({ wsId, projectId: fixedProjectId }: WorkspaceLabelsPanelProps) {
  const [search, setSearch] = useState('')
  const [term, setTerm] = useState('')
  const [projectId, setProjectId] = useState(fixedProjectId ?? '')
  const [includeArchived, setIncludeArchived] = useState(false)
  const [offset, setOffset] = useState(0)

  // Debounce the search term so a fast typist does not fire a request per
  // keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setTerm(search.trim()), 250)
    return () => clearTimeout(timer)
  }, [search])

  const labelsQ = useWorkspaceLabels(wsId, {
    q: term,
    projectId: projectId || undefined,
    includeArchived,
    limit: PAGE_SIZE,
    offset,
  })

  const boards = labelsQ.data?.boards ?? []
  const labels = labelsQ.data?.labels ?? []
  const total = labelsQ.data?.total ?? 0

  // A board filter that points at a board the caller can no longer see (a
  // workspace switch, an archived board) must not leave an empty screen
  // behind — clear it instead.
  useEffect(() => {
    if (!projectId || fixedProjectId) return
    if (!labelsQ.data) return
    if (!boards.some((b) => b.id === projectId)) setProjectId('')
  }, [projectId, boards, labelsQ.data])

  // A search or a filter change starts a new result set.
  useEffect(() => {
    setOffset(0)
  }, [term, projectId, includeArchived])

  if (labelsQ.isLoading) {
    return (
      <p className="text-surface-500 p-4 text-sm" role="status">
        Loading labels…
      </p>
    )
  }

  if (labelsQ.isError) {
    return (
      <div className="p-4" role="alert">
        <p className="text-sm text-red-600 dark:text-red-300">Could not load the labels of this workspace.</p>
        <button type="button" className="btn-ghost mt-2" onClick={() => void labelsQ.refetch()}>
          Retry
        </button>
      </div>
    )
  }

  const boardById = new Map(boards.map((b) => [b.id, b] as const))

  return (
    <div className="space-y-4">
      <p className="text-surface-500 text-xs">
        Labels belong to the workspace. A label is available on every board unless you limit it to some
        boards.
      </p>

      <section className="card p-4" aria-label="Filter labels">
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex-1 min-w-[12rem]">
            <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Search</span>
            <input
              type="search"
              className="input mt-1"
              placeholder="Label name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <label>
            <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Available on</span>
            <select
              className="input mt-1"
              value={projectId}
              disabled={!!fixedProjectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <option value="">All boards</option>
              {boards.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} ({b.key})
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 pb-2 text-xs text-surface-600 dark:text-surface-300">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            Show archived
          </label>
        </div>
      </section>

      <CreateLabelSection wsId={wsId} boards={boards} canEdit={!!labelsQ.data?.canEdit} />

      {labels.length === 0 ? (
        <div className="rounded-lg border border-dashed border-surface-200 p-12 text-center text-sm text-surface-500 dark:border-surface-700">
          No labels yet. Create one to start tagging tasks.
        </div>
      ) : (
        <ul className="space-y-2" aria-label="Labels">
          {labels.map((label) => (
            <LabelRow
              key={label.id}
              wsId={wsId}
              label={label}
              boards={boards}
              canEdit={!!labelsQ.data?.canEdit}
              boardById={boardById}
            />
          ))}
        </ul>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-surface-500">
          <span>
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <span className="flex gap-2">
            <button
              type="button"
              className="btn-ghost min-h-11"
              disabled={offset === 0 || labelsQ.isFetching}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn-ghost min-h-11"
              disabled={offset + PAGE_SIZE >= total || labelsQ.isFetching}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </button>
          </span>
        </div>
      )}
    </div>
  )
}

/** Create a workspace label, on every board or on the chosen ones. */
function CreateLabelSection({
  wsId,
  boards,
  canEdit,
}: {
  wsId: string
  boards: WorkspaceLabelBoard[]
  canEdit: boolean
}) {
  const [name, setName] = useState('')
  const [color, setColor] = useState('#9698F9')
  const [projectIds, setProjectIds] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const create = useCreateLabel(wsId)

  const dirty = name.trim().length > 0
  useUnsavedChangesGuard(dirty && !create.isPending)

  if (!canEdit) {
    return <p className="text-surface-500 text-xs">Your role in this workspace cannot create labels.</p>
  }

  return (
    <section className="card p-4" aria-label="Create label">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-surface-500">New label</h2>
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={async (e) => {
          e.preventDefault()
          if (!name.trim()) return
          setError(null)
          try {
            await create.mutateAsync({ name: name.trim(), color, projectIds })
            setName('')
            setProjectIds([])
            toast('Label created', { kind: 'success' })
          } catch (err) {
            const msg = err instanceof ApiError ? err.message : (err as Error).message
            setError(msg)
            toast(msg, { kind: 'error' })
          }
        }}
      >
        <label className="flex-1 min-w-[12rem]">
          <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Name</span>
          <input
            className="input mt-1"
            placeholder="e.g. Bug"
            maxLength={40}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Colour</span>
          <input
            type="color"
            aria-label="Colour"
            className="mt-1 h-9 w-12 cursor-pointer rounded border border-surface-300 dark:border-surface-700"
            value={color}
            onChange={(e) => setColor(e.target.value)}
          />
        </label>
        <div>
          <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Boards</span>
          <BoardsPicker
            className="mt-1"
            label="Boards for the new label"
            boards={boards}
            value={projectIds}
            onChange={setProjectIds}
          />
        </div>
        <button type="submit" className="btn-primary" disabled={create.isPending || !name.trim()}>
          {create.isPending ? 'Creating…' : 'Create label'}
        </button>
      </form>
      {error && (
        <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-300">
          {error}
        </p>
      )}
    </section>
  )
}

/** Summary of the boards a label is on. */
function boardsSummary(projectIds: string[], boardById: Map<string, WorkspaceLabelBoard>) {
  if (!projectIds.length) return 'All boards'
  const keys = projectIds.map((id) => boardById.get(id)?.key).filter(Boolean)
  return keys.length ? keys.join(', ') : `${projectIds.length} ${projectIds.length === 1 ? 'board' : 'boards'}`
}

/**
 * Pick the boards a label is on. An empty value means every board: all rows
 * show checked, and unchecking one keeps the others. The last board cannot be
 * unchecked; "All boards" goes back to every board.
 */
function BoardsPicker({
  boards,
  value,
  onChange,
  label,
  disabled,
  className,
}: {
  boards: WorkspaceLabelBoard[]
  value: string[]
  onChange: (projectIds: string[]) => void
  label: string
  disabled?: boolean
  className?: string
}) {
  const active = boards.filter((b) => !b.archivedAt)
  const all = value.length === 0
  const shown = all ? active.map((b) => b.id) : value
  const boardById = new Map(boards.map((b) => [b.id, b] as const))
  return (
    <MultiSelectMenu
      className={className}
      label={label}
      disabled={disabled}
      searchable={active.length > 8}
      options={active.map((b) => ({ id: b.id, label: b.name, hint: b.key }))}
      value={shown}
      allLabel="All boards"
      allSelected={all}
      onToggleAll={() => onChange([])}
      onChange={(next) => {
        if (!next.length) return
        onChange(next.length === active.length ? [] : next)
      }}
      emptyText="No boards in this workspace."
      trigger={<span className="max-w-[14rem] truncate">{boardsSummary(value, boardById)}</span>}
    />
  )
}

/** One label: name, colour, boards, usage and the edit/archive actions. */
function LabelRow({
  wsId,
  label,
  boards,
  boardById,
  canEdit,
}: {
  wsId: string
  label: WorkspaceLabel
  boards: WorkspaceLabelBoard[]
  boardById: Map<string, WorkspaceLabelBoard>
  canEdit: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(label.name)
  const [error, setError] = useState<string | null>(null)
  const [pendingArchive, setPendingArchive] = useState(false)
  const update = useUpdateLabel(wsId)
  const archive = useArchiveLabel(wsId)

  const canWrite = canEdit
  const dirty = editing && name.trim() !== label.name
  useUnsavedChangesGuard(dirty && !update.isPending)

  function cancel() {
    setName(label.name)
    setEditing(false)
    setError(null)
  }

  async function save() {
    if (!dirty) return
    setError(null)
    try {
      await update.mutateAsync({ labelId: label.id, patch: { name: name.trim() } })
      setEditing(false)
      toast('Label updated', { kind: 'success' })
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Could not update the label.'
      setError(msg)
      toast(msg, { kind: 'error' })
    }
  }

  function setBoards(projectIds: string[]) {
    update.mutate(
      { labelId: label.id, patch: { projectIds } },
      {
        onSuccess: () => toast('Label boards updated', { kind: 'success' }),
        onError: (e) => {
          const msg = e instanceof ApiError ? e.message : 'Could not update the label.'
          setError(msg)
          toast(msg, { kind: 'error' })
        },
      },
    )
  }

  function recolour(color: string) {
    if (color === label.color) return
    update.mutate(
      { labelId: label.id, patch: { color } },
      {
        onSuccess: () => toast('Label updated', { kind: 'success' }),
        onError: (e) => {
          const msg = e instanceof ApiError ? e.message : 'Could not update the label.'
          setError(msg)
          toast(msg, { kind: 'error' })
        },
      },
    )
  }

  return (
    <li className="card p-3" data-testid="workspace-label">
      <div className="flex flex-wrap items-center gap-3">
        <span
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: label.color }}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          {editing ? (
            <input
              className="input h-7 py-0 text-sm"
              aria-label={`Name for ${label.name}`}
              maxLength={40}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          ) : (
            <div className="truncate text-sm font-semibold">
              {label.name}
              {label.archivedAt && (
                <span className="ml-2 rounded bg-surface-200 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-surface-600 dark:bg-surface-700 dark:text-surface-300">
                  Archived
                </span>
              )}
            </div>
          )}
          <div className="text-xs text-surface-500">
            {boardsSummary(label.projectIds, boardById)} · {label.taskCount}{' '}
            {label.taskCount === 1 ? 'task' : 'tasks'}
          </div>
        </div>

        {canWrite && (
          <div className="flex flex-wrap items-center gap-1">
            <BoardsPicker
              label={`Boards for ${label.name}`}
              boards={boards}
              value={label.projectIds}
              onChange={setBoards}
              disabled={update.isPending}
            />
            <label className="flex items-center gap-1" title="Change colour">
              <span className="sr-only">Colour for {label.name}</span>
              <input
                type="color"
                aria-label={`Colour for ${label.name}`}
                className="h-8 w-9 cursor-pointer rounded border border-surface-300 dark:border-surface-700"
                value={label.color}
                onChange={(e) => recolour(e.target.value)}
              />
            </label>
            <button type="button" className="btn-ghost min-h-11 text-xs" onClick={() => setEditing(true)}>
              Rename
            </button>
            <button
              type="button"
              className="btn-ghost min-h-11 text-xs text-red-600 dark:text-red-300"
              onClick={() => setPendingArchive(true)}
            >
              Archive
            </button>
          </div>
        )}
      </div>

      {editing && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn-primary min-h-11 text-xs"
            disabled={update.isPending || !dirty}
            onClick={() => void save()}
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="btn-ghost min-h-11 text-xs" disabled={update.isPending} onClick={cancel}>
            Cancel
          </button>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-300">
          {error}
        </p>
      )}

      <ConfirmDialog
        open={pendingArchive}
        title={`Archive "${label.name}"?`}
        body={
          <div className="text-surface-600 dark:text-surface-300 space-y-2 text-sm">
            <p>
              This hides the label from the label lists of this workspace. It is not
              deleted: adding a label with the same name to a task brings it back.
            </p>
            <p>
              {label.taskCount === 0
                ? 'No task is tagged with this label.'
                : `${label.taskCount} ${label.taskCount === 1 ? 'task is' : 'tasks are'} still tagged with it and keep showing the label until it is removed from them.`}
            </p>
          </div>
        }
        confirmLabel="Archive label"
        destructive
        busy={archive.isPending}
        onClose={() => !archive.isPending && setPendingArchive(false)}
        onConfirm={async () => {
          try {
            await archive.mutateAsync(label.id)
            setPendingArchive(false)
            toast(`Label "${label.name}" archived`, { kind: 'success' })
          } catch (e) {
            const msg = e instanceof ApiError ? e.message : 'Could not archive the label.'
            setError(msg)
            toast(msg, { kind: 'error' })
          }
        }}
      />
    </li>
  )
}
